import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import Sidebar from '../Sidebar';
import { ArrowLeft, Users, Search, Download, RefreshCw } from 'lucide-react';

// =====================================================================
// CONSULTA DE COLABORADORES
// =====================================================================
// Tela cheia acessível pelo card "Colaboradores" do Acesso Rápido (Home).
// Lista os funcionários ATIVOS no escopo do usuário com NOME, LOJA, CARGO e
// TEMPO DE EMPRESA. Lê /api/home/colaboradores?q= — mesmo recorte de empresa/
// loja dos demais quadros (usuário de loja vê só a sua). Busca por nome.

const COR = {
    paginaFundo: '#f2f2f2',
    branco: '#ffffff',
    vermelhoEscuro: '#990605',
    iconeFundo: '#FFF2F2',
    texto: '#1a1a1a',
    bordaSuave: 'rgba(199,194,194,0.45)',
};
const FONTE = "'Raleway', sans-serif";
const SOMBRA = '0 4px 8px rgba(0,0,0,0.12)';

// Tempo de empresa (meses inteiros) → "Xa Ym". Trata nulo/negativo (admissão
// ausente ou futura) e o caso de menos de um mês.
function formatarTempo(meses) {
    if (meses === null || meses === undefined || meses < 0) return '—';
    if (meses === 0) return '< 1 mês';
    const anos = Math.floor(meses / 12);
    const m = meses % 12;
    if (anos > 0 && m > 0) return `${anos}a ${m}m`;
    if (anos > 0) return `${anos}a`;
    return `${m}m`;
}

function ColaboradoresConsulta() {
    const navigate = useNavigate();
    const [isCollapsed, setIsCollapsed] = useState(window.innerWidth <= 768);
    const [colaboradores, setColaboradores] = useState([]);
    const [atualizadoEm, setAtualizadoEm] = useState(null);
    const [busca, setBusca] = useState('');
    const [carregando, setCarregando] = useState(true);

    useEffect(() => {
        const onResize = () => { if (window.innerWidth <= 768) setIsCollapsed(true); };
        window.addEventListener('resize', onResize);
        onResize();
        return () => window.removeEventListener('resize', onResize);
    }, []);

    const carregar = useCallback(async (q) => {
        setCarregando(true);
        try {
            const url = '/api/home/colaboradores' + (q ? `?q=${encodeURIComponent(q)}` : '');
            const r = await fetch(url, { headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` } });
            const data = r.ok ? await r.json() : { colaboradores: [], atualizadoEm: null };
            setColaboradores(data.colaboradores || []);
            setAtualizadoEm(data.atualizadoEm || null);
        } catch (e) {
            console.error('[Colaboradores] carregar:', e);
            setColaboradores([]);
        } finally {
            setCarregando(false);
        }
    }, []);

    // Busca com debounce simples por digitação
    useEffect(() => {
        const t = setTimeout(() => carregar(busca.trim()), 300);
        return () => clearTimeout(t);
    }, [busca, carregar]);

    // Exporta para Excel a lista ATUAL (respeitando a busca por nome). Usa o
    // SheetJS (já no projeto). As colunas espelham a tabela; o tempo de empresa
    // sai formatado ("Xa Ym") como na tela. Linhares fica sem nascimento ("—").
    const exportarExcel = () => {
        if (colaboradores.length === 0) return;
        const linhas = colaboradores.map(c => ({
            Nome: c.nome,
            Loja: c.loja,
            Cargo: c.cargo,
            'Tempo de empresa': formatarTempo(c.tempoMeses),
            'Data de nascimento': c.nascimento || '—',
            Admissão: c.admissao || '—',
        }));
        const ws = XLSX.utils.json_to_sheet(linhas);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Colaboradores');
        // Nome do arquivo com a data de hoje (AAAA-MM-DD) para versionar exportações.
        const hoje = new Date().toISOString().slice(0, 10);
        XLSX.writeFile(wb, `Colaboradores_${hoje}.xlsx`);
    };

    const getMarginLeft = () => (window.innerWidth <= 768 ? '0px' : (isCollapsed ? '80px' : '260px'));

    return (
        <div style={{ display: 'flex', minHeight: '100vh', backgroundColor: COR.paginaFundo, fontFamily: FONTE }}>
            <Sidebar isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} />

            <div style={{ flex: 1, marginLeft: getMarginLeft(), transition: 'margin-left 0.3s ease', padding: '28px 32px', minWidth: 0 }}>

                {/* Voltar */}
                <button
                    onClick={() => navigate('/home')}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', cursor: 'pointer', color: COR.vermelhoEscuro, fontFamily: FONTE, fontWeight: 600, fontSize: '14px', padding: 0, marginBottom: '14px' }}
                >
                    <ArrowLeft size={18} /> Voltar para a Home
                </button>

                {/* Cabeçalho: título + contagem + atualização + busca + exportar */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px', marginBottom: '20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                        <Users size={28} color={COR.vermelhoEscuro} />
                        <h1 style={{ margin: 0, fontSize: '28px', fontWeight: 600, color: COR.texto }}>Colaboradores</h1>
                        <span style={{ fontSize: '13px', color: '#777' }}>
                            {carregando ? 'carregando…' : `${colaboradores.length} ativo${colaboradores.length === 1 ? '' : 's'}`}
                        </span>
                        {/* Última atualização do BI (recarga diária da base de RH). */}
                        {atualizadoEm && (
                            <span title="Horário da última recarga diária da base de RH" style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '12px', color: '#777', backgroundColor: COR.iconeFundo, borderRadius: '6px', padding: '3px 9px' }}>
                                <RefreshCw size={13} color={COR.vermelhoEscuro} /> Atualizado em {atualizadoEm}
                            </span>
                        )}
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', backgroundColor: COR.branco, borderRadius: '10px', boxShadow: SOMBRA, padding: '0 12px', height: '44px', width: '320px', maxWidth: '100%' }}>
                            <Search size={18} color="#7a7a7a" />
                            <input
                                value={busca}
                                onChange={(e) => setBusca(e.target.value)}
                                placeholder="Buscar pelo nome…"
                                style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', fontFamily: FONTE, fontSize: '14px', color: COR.texto }}
                            />
                        </div>
                        <button
                            onClick={exportarExcel}
                            disabled={carregando || colaboradores.length === 0}
                            title="Exportar a lista atual para Excel"
                            style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', height: '44px', padding: '0 16px', border: 'none', borderRadius: '10px', boxShadow: SOMBRA, backgroundColor: COR.vermelhoEscuro, color: '#fff', fontFamily: FONTE, fontWeight: 700, fontSize: '14px', cursor: (carregando || colaboradores.length === 0) ? 'not-allowed' : 'pointer', opacity: (carregando || colaboradores.length === 0) ? 0.5 : 1 }}
                        >
                            <Download size={18} /> Exportar
                        </button>
                    </div>
                </div>

                {/* Tabela */}
                <div style={{ backgroundColor: COR.branco, borderRadius: '10px', boxShadow: SOMBRA, overflow: 'hidden' }}>
                    <div style={{ maxHeight: 'calc(100vh - 230px)', overflowY: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                            <thead>
                                <tr style={{ position: 'sticky', top: 0, backgroundColor: '#faf7f7', zIndex: 1 }}>
                                    <Th>Nome</Th>
                                    <Th>Loja</Th>
                                    <Th>Cargo</Th>
                                    <Th>Tempo de empresa</Th>
                                    <Th>Nascimento</Th>
                                </tr>
                            </thead>
                            <tbody>
                                {!carregando && colaboradores.length === 0 && (
                                    <tr><td colSpan={5} style={{ padding: '28px', textAlign: 'center', color: '#999' }}>Nenhum colaborador encontrado.</td></tr>
                                )}
                                {colaboradores.map((c, i) => (
                                    <tr key={i} style={{ borderTop: `1px solid ${COR.bordaSuave}` }}>
                                        <Td><span style={{ fontWeight: 600, color: COR.texto }}>{c.nome}</span></Td>
                                        <Td>
                                            <span style={{ display: 'inline-block', backgroundColor: COR.iconeFundo, color: COR.vermelhoEscuro, fontWeight: 700, fontSize: '12px', borderRadius: '6px', padding: '2px 8px' }}>
                                                {c.loja}
                                            </span>
                                        </Td>
                                        <Td><span style={{ color: '#555' }}>{c.cargo}</span></Td>
                                        <Td>
                                            <span style={{ fontWeight: 600, color: COR.texto }}>{formatarTempo(c.tempoMeses)}</span>
                                            {c.admissao && <span style={{ color: '#999', fontSize: '12px', marginLeft: '8px' }}>desde {c.admissao}</span>}
                                        </Td>
                                        <Td><span style={{ color: '#555' }}>{c.nascimento || '—'}</span></Td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}

// Célula de cabeçalho e de corpo da tabela
function Th({ children }) {
    return <th style={{ textAlign: 'left', padding: '14px 18px', fontSize: '12px', fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{children}</th>;
}
function Td({ children }) {
    return <td style={{ padding: '12px 18px' }}>{children}</td>;
}

export default ColaboradoresConsulta;
